/**
 * RemittanceEmailDialog
 * ---------------------
 * Pick invoices on the audit queue, then send the supplier the record of what
 * was paid: amount, date, method and the bank confirmation number against each
 * invoice, with paid-in-full or partial stated plainly. Sends from the portal
 * or writes the letter into Outlook drafts. Every send is logged on the
 * invoices and payments it covers.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/App";

const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const RemittanceEmailDialog = ({ open, invoiceIds = [], token, onClose, onSent }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    if (!open || !invoiceIds.length) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/invoice-audit/remittance/preview`,
        { invoice_ids: invoiceIds }, { headers });
      setData(r.data);
      setTo((r.data.to || []).join(", "));
      setSubject(r.data.subject || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build that remittance");
      onClose?.();
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(invoiceIds), token]);

  useEffect(() => { load(); }, [load]);

  if (!open) return null;

  const send = async (channel) => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/invoice-audit/remittance/send`, {
        invoice_ids: invoiceIds,
        to: to.split(",").map((s) => s.trim()).filter(Boolean),
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
        subject, note: note || null, channel,
      }, { headers });
      if (channel === "outlook_draft") {
        toast.success("Remittance written to your Outlook drafts");
        if (r.data?.web_link) window.open(r.data.web_link, "_blank", "noopener");
      } else {
        toast.success(`Remittance emailed — ${money(r.data?.remittance?.amount_remitted)}`, {
          description: `Logged on ${r.data?.remittance?.invoice_numbers?.length} invoice(s) as ${r.data?.remittance?.id}` });
      }
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that remittance",
        { duration: 10000 });
    } finally { setSending(false); }
  };

  const t = data?.totals || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3"
      onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="remittance-dialog">
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-black text-slate-900 inline-flex items-center gap-2">
              <Mail className="w-4 h-4 text-sky-600" /> Email the payment record
            </h3>
            <p className="text-[11px] text-slate-500">
              {data?.supplier_name || "Supplier"} · {t.invoice_count || 0} invoice(s) ·{" "}
              {money(t.paid)} remitted
              {t.balance > 0.01 ? ` · ${money(t.balance)} still open` : " · settled in full"}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="remittance-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 overflow-y-auto space-y-3">
          {loading && (
            <p className="text-sm text-slate-400 text-center py-6">
              <Loader2 className="w-4 h-4 animate-spin inline" /> Building the letter…
            </p>
          )}

          {!loading && data && (
            <>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  To
                  <Input value={to} onChange={(e) => setTo(e.target.value)}
                    placeholder="accounting@supplier.com"
                    className="h-8 text-xs mt-0.5 font-normal normal-case"
                    data-testid="remittance-to" />
                </label>
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Cc
                  <Input value={cc} onChange={(e) => setCc(e.target.value)}
                    className="h-8 text-xs mt-0.5 font-normal normal-case"
                    data-testid="remittance-cc" />
                </label>
              </div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Subject
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="h-8 text-xs mt-0.5 font-normal normal-case"
                  data-testid="remittance-subject" />
              </label>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Note to the supplier (optional)
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Anything you want said above the table — who to call, what to apply it to"
                  className="text-xs mt-0.5 font-normal normal-case"
                  data-testid="remittance-note" />
              </label>

              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <table className="w-full text-[11.5px]">
                  <thead className="bg-slate-50">
                    <tr>
                      {["Invoice", "Payment & confirmation", "Billed", "Paid", "Status"].map((h) => (
                        <th key={h} className="text-left px-2 py-1.5 text-[9px] font-black uppercase tracking-wider text-slate-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(data.invoices || []).map((r) => (
                      <tr key={r.invoice_id} data-testid={`remittance-row-${r.invoice_id}`}>
                        <td className="px-2 py-1.5 font-bold text-slate-900">
                          {r.invoice_number}
                          {r.po_number && <span className="block text-[10px] font-normal text-slate-500">PO {r.po_number}</span>}
                        </td>
                        <td className="px-2 py-1.5 text-slate-600">
                          {r.payments?.length ? r.payments.map((p, i) => (
                            <span key={i} className="block">
                              {(p.method || "").replace(/_/g, " ").toUpperCase()} {p.payment_date}
                              {p.reference && <strong> · {p.reference}</strong>}
                            </span>
                          )) : <em className="text-amber-700">no payment recorded</em>}
                        </td>
                        <td className="px-2 py-1.5 text-right">{money(r.amount)}</td>
                        <td className="px-2 py-1.5 text-right font-bold">{money(r.paid_total)}</td>
                        <td className={`px-2 py-1.5 text-right font-bold text-[10.5px] ${
                          r.settled ? "text-emerald-700" : "text-amber-700"}`}>
                          {r.settled ? "Paid in full" : `Partial · ${money(r.balance)} left`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!data.payments?.length && (
                <p className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2">
                  No payment is recorded against these invoices yet. Record the payment first,
                  or the supplier gets a remittance with nothing on it.
                </p>
              )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="outline" disabled={sending || loading}
            onClick={() => send("outlook_draft")} data-testid="remittance-draft">
            Draft in Outlook
          </Button>
          <Button size="sm" disabled={sending || loading}
            className="bg-sky-700 hover:bg-sky-800" onClick={() => send("send")}
            data-testid="remittance-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                     : <Send className="w-3.5 h-3.5 mr-1" />}
            Send the record of payment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RemittanceEmailDialog;
